<ng-container *ngIf="(twoFactorAuthStatus$ | async) === TwoFactorAuthStatus.DISABLED">
  <p class="mr-large-b">{{ 'plus.profile.twoFactorAuth.intro' | finLocalization }}</p>
  <p class="medium-type mr-small-b">{{ 'plus.profile.twoFactorAuth.select' | finLocalization }}:</p>
</ng-container>

<div #fcomNotification *ngIf="hasError">
  <fcom-notification
    aria-live="polite"
    class="mr-small-b"
    [theme]="NotificationTheme.ALERT"
    [layout]="NotificationLayout.BLOCK"
    [isClosable]="true"
    [i18n]="{ closeAriaLabel: 'plus.profile.closeNotification' }"
    (closeClick)="onCloseError()"
  >
    {{ 'plus.profile.twoFactorAuthFailed' | finLocalization }}
  </fcom-notification>
</div>

<div *ngIf="(twoFactorAuthStatus$ | async) === TwoFactorAuthStatus.ENABLED" data-testid="twoFactorAuthEnabledContainer">
  <fcom-notification
    [buttonStyle]="NotificationButtonStyle.LINK"
    [linkText]="
      enabledBackButtonUrl
        ? ('plus.profile.twoFactorAuth.enabledNotification.continueLinkingYourAccountsButton' | finLocalization)
        : ''
    "
    [linkUrl]="enabledBackButtonUrl"
    (linkClick)="onBackButtonLinkClick()"
    class="mr-small-b"
    data-testid="loyalty-two-factor-auth-toggle__content__notification"
  >
    {{ 'plus.profile.twoFactorAuthEnabled' | finLocalization }}
  </fcom-notification>
  <div *finLet="aviosLinkingStatus$ | async as aviosLinkingStatus" class="align-right">
    <fcom-button
      [text]="'plus.profile.disable' | finLocalization"
      (clickBtn)="aviosLinkingStatus === AviosAccountLinkingStatus.ACTIVE ? (isMFAModalOpened = true) : disableGAuth()"
      data-testid="loyalty-two-factor-auth-toggle__content__disable-gauth-button"
    >
    </fcom-button>
  </div>
</div>

<div
  *ngIf="(twoFactorAuthStatus$ | async) === TwoFactorAuthStatus.LOADING"
  class="relative loader"
  data-testid="twoFactorAuthLoadingContainer"
>
  <fcom-loader [loaderType]="LoaderTheme.INLINE"></fcom-loader>
</div>

<fcom-tabs
  *ngIf="(twoFactorAuthStatus$ | async) === TwoFactorAuthStatus.DISABLED"
  [theme]="TabTheme.DEFAULT"
  [prevButtonAriaLabel]="'carousel.previous' | finLocalization"
  [nextButtonAriaLabel]="'carousel.next' | finLocalization"
  data-testid="twoFactorAuthDisabledContainer"
>
  <fcom-tab [title]="'plus.profile.twoFactorAuth.app.title' | finLocalization">
    <ng-container *ngIf="authState === AuthState.CHOOSE_TYPE">
      <ol class="ms-large-l">
        <li class="medium-type ms-small-b">
          <span class="no-outline" tabindex="-1" #appFirstStep
            >{{ 'plus.profile.twoFactorAuth.app.step.0' | finLocalization }}:</span
          >
          <cms-unstyled-fragment
            [contentUrl$]="'fragments.twoFactorAuth.url' | finLocalization | finToObservable"
            class="light-type block ms-medium-t ms-large-b"
          ></cms-unstyled-fragment>
        </li>
        <li class="medium-type ms-small-b">{{ 'plus.profile.twoFactorAuth.app.step.1' | finLocalization }}</li>
      </ol>
      <div class="align-right mr-xxsmall-r">
        <fcom-button
          data-testid="two-factor-auth-continue-button"
          [text]="'plus.profile.twoFactorAuth.continue' | finLocalization"
          (clickBtn)="selectTwoFactorAuthType(TwoFactorAuthType.APP)"
        >
        </fcom-button>
      </div>
    </ng-container>
    <form
      *ngIf="authState === AuthState.VERIFY || authState === AuthState.INCORRECT_CODE || authState === AuthState.ERROR"
      [formGroup]="validateGAuthForm"
      class="form-container service-form grow"
      (ngSubmit)="validateCode()"
    >
      <ol start="3" class="ms-large-l">
        <li class="medium-type ms-small-b no-outline" tabindex="-1" #appThirdStep>
          {{ 'plus.profile.twoFactorAuth.app.step.2' | finLocalization }}
        </li>
        <li class="medium-type ms-small-b">{{ 'plus.profile.twoFactorAuth.app.step.3' | finLocalization }}</li>
      </ol>
      <span class="block pr-medium-t font-body-1 nordic-blue-900-text">{{
        'SERVICE_FORMS.serviceForms.mandatoryFields' | finLocalization
      }}</span>
      <div class="pr-xxsmall-y" *ngIf="qrCodeImageUrl$ | async as qrCodeImageUrl">
        <img alt="" [src]="qrCodeImageUrl" />
      </div>
      <div class="light-type font-body-1 ms-small-b">
        {{ 'plus.profile.secretKey' | finLocalization }}: {{ secretKey }}
      </div>
      <div class="flex form-subgroup">
        <fin-form-input-text
          class="mr-xsmall-t font-body-1"
          [parentForm]="validateGAuthForm"
          [controlName]="'securityCode'"
          [translationPrefix]="'plus.profile.securityCode'"
        ></fin-form-input-text>
      </div>
      <fcom-notification
        *ngIf="authState === AuthState.INCORRECT_CODE || authState === AuthState.ERROR"
        aria-live="polite"
        class="pr-small-b mr-clear"
        [theme]="NotificationTheme.ALERT"
        [layout]="NotificationLayout.BLOCK"
        [iconName]="SvgLibraryIcon.INFO"
        [isClosable]="true"
        [i18n]="{ closeAriaLabel: 'plus.profile.closeNotification' }"
        (closeClick)="authState = AuthState.VERIFY"
      >
        {{
          (authState === AuthState.INCORRECT_CODE
            ? 'plus.profile.securityCode.pattern'
            : 'plus.profile.twoFactorAuthFailed'
          ) | finLocalization
        }}
      </fcom-notification>
      <div class="flex flex--end mr-xxsmall-r">
        <fcom-button
          [text]="'plus.profile.twoFactorAuth.cancel' | finLocalization"
          [theme]="ButtonTheme.LIGHT_SECONDARY_OUTLINE"
          (clickBtn)="resetAuthState()"
        >
        </fcom-button>
        <fcom-button
          data-testid="two-factor-auth-validate-button"
          [text]="'plus.profile.validate' | finLocalization"
          [type]="ButtonType.SUBMIT"
          [disabled]="!validateGAuthForm.valid"
          class="ms-small-l"
        >
        </fcom-button>
      </div>
    </form>
  </fcom-tab>
  <fcom-tab [title]="'plus.profile.twoFactorAuth.sms.title' | finLocalization">
    <ng-container *ngIf="authState === AuthState.CHOOSE_TYPE || authState === AuthState.VERIFY">
      <ol class="ms-large-l">
        <li class="medium-type ms-small-b">{{ 'plus.profile.twoFactorAuth.sms.step.0' | finLocalization }}</li>
        <li class="medium-type ms-small-b">{{ 'plus.profile.twoFactorAuth.sms.step.1' | finLocalization }}</li>
      </ol>
      <div class="align-right mr-xxsmall-r">
        <fcom-button [text]="'plus.profile.enable' | finLocalization" (clickBtn)="openConfirmModal($event)">
        </fcom-button>
      </div>
    </ng-container>
  </fcom-tab>
</fcom-tabs>

<fcom-modal
  #modal
  [(open)]="isConfirmModalOpen"
  [title]="
    ((parsedMobilePhone$ | async)
      ? 'plus.profile.twoFactorAuth.confirmationModalSms.heading'
      : 'plus.profile.twoFactorAuth.confirmationModalSms.headingMissingPhoneNr'
    ) | finLocalization
  "
  [buttons]="!!(parsedMobilePhone$ | async) ? ModalButtons.CUSTOM : ModalButtons.CLOSE"
  [confirmEnabled]="!!(parsedMobilePhone$ | async)"
  [fitToContent]="true"
  (confirm)="selectTwoFactorAuthType(TwoFactorAuthType.SMS)"
>
  <div class="pr-large">
    <p *ngIf="parsedMobilePhone$ | async as phoneNumber">
      {{ 'plus.profile.twoFactorAuth.confirmationModalSms.body' | finLocalization: { phoneNumber } }}
    </p>
    <p *ngIf="!!(parsedMobilePhone$ | async) !== true">
      {{ 'plus.profile.twoFactorAuth.confirmationModalSms.bodyMissingPhoneNr' | finLocalization }}
    </p>
  </div>

  <ng-container buttons>
    <fcom-button
      class="close-modal-btn mr-xsmall-r"
      [text]="'actions.cancel' | finLocalization"
      [disabled]="!modal.open || modal.forceRemainOpen"
      [theme]="ButtonTheme.LIGHT_SECONDARY_OUTLINE"
      (clickBtn)="modal.onClose()"
    >
    </fcom-button>
    <fcom-button
      class="confirm-modal-btn"
      [text]="'plus.profile.enable' | finLocalization"
      [disabled]="!modal.open || !modal.confirmEnabled || modal.forceRemainOpen"
      (clickBtn)="modal.onConfirm()"
    >
    </fcom-button>
  </ng-container>
</fcom-modal>

<fcom-modal
  #disableConfirmationWhenAviosLinkedAccountsModal
  [(open)]="isMFAModalOpened"
  [title]="'plus.profile.twoFactorAuth.disablingConfirmation.title' | finLocalization"
  [buttons]="ModalButtons.CUSTOM"
  [fitToContent]="true"
  (confirm)="disableGAuth()"
  data-testid="loyalty-two-factor-auth-toggle__mfa-modal"
>
  <div class="pr-large font-body-1">
    {{ 'plus.profile.twoFactorAuth.disablingConfirmation.bodyText' | finLocalization }}
  </div>

  <div buttons>
    <div class="flex flex--end">
      <fcom-button
        class="close-modal-btn mr-xsmall-r"
        [text]="'actions.cancel' | finLocalization"
        [theme]="ButtonTheme.LIGHT_SECONDARY_OUTLINE"
        (clickBtn)="disableConfirmationWhenAviosLinkedAccountsModal.onClose()"
      >
      </fcom-button>
      <fcom-button
        class="confirm-modal-btn"
        [text]="'plus.profile.twoFactorAuth.disablingConfirmation.disableButtonText' | finLocalization"
        (clickBtn)="disableConfirmationWhenAviosLinkedAccountsModal.onConfirm()"
      >
      </fcom-button>
    </div>
  </div>
</fcom-modal>
